import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/navbar/Navbar";
import Sidebar from "../../components/sidebar/Sidebar";
import { Edit, Delete, ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { authHeader } from "../../utils/authHeader";
import { API_BASE_URL } from "../../config";
import "./editProfile.scss";

const EditProfile = () => {
  const { currentUser, setCurrentUser } = useContext(AuthContext);
  const [originalForm, setOriginalForm] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    personal: false,
    contacto: false,
    laboral: false,
    otros: false,
    seguridad: false
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Tu perfil ha sido actualizado correctamente.");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    fechaIngreso: "",
    fechaNacimiento: "",
    phone: "",
    celularCorporativo: "",
    country: "",
    direccionLaboral: "",
    direccionPersonal: "",
    cargo: "",
    supervisor: "",
    area: "",
    linkedIn: "",
    language: "",
    estadoCivil: "",
    age: "",
    existingProfilePicture: "",
    password: ""
  });
  const [file, setFile] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const navigate = useNavigate();
  const fileInputRef = React.useRef(null);
  const photoWrapperRef = React.useRef(null);

  useEffect(() => {
    if (!currentUser?.id) return;
    fetch(`${API_BASE_URL}/api/users/${currentUser.id}`, {
      headers: { ...authHeader() },
    })
      .then(r => r.json())
      .then(u => {
        const parseDateField = (val) => {
          if (!val) return "";
          const s = val instanceof Date ? val.toISOString() : String(val);
          return s.split('T')[0];
        };
        const userData = {
          name: u.name || "",
          username: u.username || "",
          email: u.email || "",
          fechaIngreso: parseDateField(u.fechaIngreso),
          fechaNacimiento: parseDateField(u.fechaNacimiento),
          phone: u.phone || "",
          celularCorporativo: u.celularCorporativo || "",
          country: u.country || "",
          direccionLaboral: u.direccionLaboral || "",
          direccionPersonal: u.direccionPersonal || "",
          cargo: u.cargo || "",
          supervisor: u.supervisor || "",
          area: u.area || "",
          linkedIn: u.linkedIn || "",
          language: u.language || "",
          estadoCivil: u.estadoCivil || "",
          age: u.age || "",
          existingProfilePicture: u.profilePicture || "",
          password: ""
        };
        setForm(userData);
        setOriginalForm(userData);
        setHasChanges(false);
        setFile(null);
      })
      .catch(console.error);
  }, [currentUser]);

  useEffect(() => {
    if (!originalForm) return;
    const changed = JSON.stringify(form) !== JSON.stringify(originalForm) || file !== null;
    setHasChanges(changed);
  }, [form, file, originalForm]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  useEffect(() => {
    const unlistenNavigation = navigate.listen?.((location, action) => {
      if (hasChanges && action === "PUSH") {
        setPendingNavigation(location);
        setShowWarningModal(true);
        return false;
      }
    });

    return () => unlistenNavigation?.();
  }, [hasChanges, navigate]);

  const handleConfirmAbandon = () => {
    setShowWarningModal(false);
    if (pendingNavigation) {
      window.location.href = pendingNavigation.pathname;
    }
  };

  const handleSaveAndNavigate = async () => {
    const form_data = new FormData();
    form_data.append("name", form.name);
    form_data.append("username", form.username);
    form_data.append("email", form.email);
    form_data.append("fechaIngreso", form.fechaIngreso);
    form_data.append("fechaNacimiento", form.fechaNacimiento);
    form_data.append("phone", form.phone);
    form_data.append("celularCorporativo", form.celularCorporativo);
    form_data.append("country", form.country);
    form_data.append("direccionLaboral", form.direccionLaboral);
    form_data.append("direccionPersonal", form.direccionPersonal);
    form_data.append("cargo", form.cargo);
    form_data.append("supervisor", form.supervisor);
    form_data.append("area", form.area);
    form_data.append("linkedIn", form.linkedIn);
    form_data.append("language", form.language);
    form_data.append("estadoCivil", form.estadoCivil);
    form_data.append("age", form.age);
    form_data.append("existingProfilePicture", form.existingProfilePicture);
    form_data.append("rol", currentUser.rol || "");
    form_data.append("points", currentUser.points ?? 0);
    if (file) {
      form_data.append("profilePicture", file);
    }
    if (form.password.trim()) {
      form_data.append("contrasena", form.password);
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/users/${currentUser.id}`,
        { method: "PUT", body: form_data, headers: { ...authHeader() } }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Error actualizando perfil");
      setCurrentUser(c => ({ ...c, ...body }));
      setHasChanges(false);
      setShowWarningModal(false);
      setSuccessMessage("✓ Tu perfil ha sido actualizado correctamente. Los cambios se han guardado en la base de datos.");
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        if (pendingNavigation) {
          window.location.href = pendingNavigation.pathname;
        }
      }, 2500);
    } catch (err) {
      setErrorMessage(err.message || "Error desconocido al guardar");
      setShowErrorModal(true);
      console.error("Frontend PUT error:", err);
      setTimeout(() => setShowErrorModal(false), 4000);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => {
      const updated = { ...f, [name]: value };
      if (name === 'fechaNacimiento' && value) {
        const today = new Date();
        const birth = new Date(value + 'T00:00:00');
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        if (age >= 0 && age <= 120) updated.age = String(age);
      }
      return updated;
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleImageSelect = (e) => {
    setFile(e.target.files[0]);
    setShowOptions(false);
  };

  const deletePhoto = () => {
    setFile(null);
    setForm(f => ({ ...f, existingProfilePicture: "" }));
    setShowOptions(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (photoWrapperRef.current && !photoWrapperRef.current.contains(e.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showOptions]);

  const laboralFields = [
    { label: "Cargo", name: "cargo", type: "text" },
    { label: "Supervisor", name: "supervisor", type: "text" },
    { label: "Área", name: "area", type: "text" }
  ];

  if (currentUser?.rol === 'admin') {
    laboralFields.unshift({ label: "Fecha de Ingreso", name: "fechaIngreso", type: "date" });
  }

  const handleSubmit = async e => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("username", form.username);
    fd.append("email", form.email);
    fd.append("fechaIngreso", form.fechaIngreso);
    fd.append("fechaNacimiento", form.fechaNacimiento);
    fd.append("phone", form.phone);
    fd.append("celularCorporativo", form.celularCorporativo);
    fd.append("country", form.country);
    fd.append("direccionLaboral", form.direccionLaboral);
    fd.append("direccionPersonal", form.direccionPersonal);
    fd.append("cargo", form.cargo);
    fd.append("supervisor", form.supervisor);
    fd.append("area", form.area);
    fd.append("linkedIn", form.linkedIn);
    fd.append("language", form.language);
    fd.append("estadoCivil", form.estadoCivil);
    fd.append("age", form.age);
    fd.append("existingProfilePicture", form.existingProfilePicture);
    fd.append("rol", currentUser.rol || "");
    fd.append("points", currentUser.points ?? 0);
    if (file) {
      fd.append("profilePicture", file);
    }
    if (form.password.trim()) {
      fd.append("contrasena", form.password);
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/users/${currentUser.id}`,
        { method: "PUT", body: fd, headers: { ...authHeader() } }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Error actualizando perfil");
      setCurrentUser(c => ({ ...c, ...body }));
      setHasChanges(false);
      setFile(null);
      setSuccessMessage("✓ Tu perfil ha sido actualizado correctamente. Los cambios se han guardado en la base de datos.");
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        navigate(`/profile/${body.username}`);
      }, 2500);
    } catch (err) {
      setErrorMessage(err.message || "Error desconocido al guardar");
      setShowErrorModal(true);
      console.error("Frontend PUT error:", err);
      setTimeout(() => setShowErrorModal(false), 4000);
    }
  };

  return (
    <div className="editProfile">
      <Navbar />
      <div className="editProfileWrapper">
        <Sidebar />
        <div className="profileRight">
          <div className="profileCover">
            <div className="profilePhotoWrapper" ref={photoWrapperRef}>
              <img
                className="profileCoverImg profileUserImg"
                src={
                  file
                    ? URL.createObjectURL(file)
                    : form.existingProfilePicture ||
                      "/assets/profileCover/DefaultProfile.jpg"
                }
                alt=""
              />
              <button 
                type="button"
                className="editPhotoBtn"
                onClick={() => setShowOptions(!showOptions)}
                title="Editar foto"
              >
                <Edit />
              </button>
              {showOptions && (
                <div className="photoOptionsMenu">
                  <button 
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="optionBtn"
                  >
                    Cambiar
                  </button>
                  <button 
                    type="button"
                    onClick={deletePhoto}
                    className="optionBtn delete"
                  >
                    <Delete /> Eliminar
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                id="file"
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleImageSelect}
              />
            </div>
          </div>
          <div className="editprofileRightBottom">
            <div className="top">
              <h1>Edición de perfil</h1>
            </div>
            <div className="bottom">
              <div className="left" />
              <div className="right">
                <form onSubmit={handleSubmit}>
                  <div className="formSection">
                    <div className="sectionHeader" onClick={() => toggleSection('personal')}>
                      <h3>Información Personal</h3>
                      <ExpandMore className={`expandIcon ${expandedSections.personal ? 'expanded' : ''}`} />
                    </div>
                    {expandedSections.personal && (
                      <div className="sectionContent">
                        {[
                          { label: "Nombre", name: "name", type: "text" },
                          { label: "Username", name: "username", type: "text" },
                          { label: "Fecha de Nacimiento", name: "fechaNacimiento", type: "date" },
                          { label: "Edad", name: "age", type: "number" },
                          { label: "Estado Civil", name: "estadoCivil", type: "text" }
                        ].map(field => (
                          <div className="formInput" key={field.name}>
                            <label>{field.label}</label>
                            <input
                              type={field.type}
                              name={field.name}
                              value={form[field.name] ?? ""}
                              onChange={handleChange}
                              {...(field.type === 'date' && field.name === 'fechaNacimiento' ? {
                                max: new Date().toISOString().split('T')[0],
                                min: "1920-01-01"
                              } : {})}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="formSection">
                    <div className="sectionHeader" onClick={() => toggleSection('contacto')}>
                      <h3>Información de Contacto</h3>
                      <ExpandMore className={`expandIcon ${expandedSections.contacto ? 'expanded' : ''}`} />
                    </div>
                    {expandedSections.contacto && (
                      <div className="sectionContent">
                        {[
                          { label: "Correo", name: "email", type: "email" },
                          { label: "Teléfono", name: "phone", type: "text" },
                          { label: "Celular Corporativo", name: "celularCorporativo", type: "text" },
                          { label: "País", name: "country", type: "text" },
                          { label: "Dirección Personal", name: "direccionPersonal", type: "text" },
                          { label: "Dirección Laboral", name: "direccionLaboral", type: "text" }
                        ].map(field => (
                          <div className="formInput" key={field.name}>
                            <label>{field.label}</label>
                            <input
                              type={field.type}
                              name={field.name}
                              value={form[field.name]}
                              onChange={handleChange}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="formSection">
                    <div className="sectionHeader" onClick={() => toggleSection('laboral')}>
                      <h3>Información Laboral</h3>
                      <ExpandMore className={`expandIcon ${expandedSections.laboral ? 'expanded' : ''}`} />
                    </div>
                    {expandedSections.laboral && (
                      <div className="sectionContent">
                        {[
                          { label: "Fecha de Ingreso", name: "fechaIngreso", type: "date" },
                          { label: "Cargo", name: "cargo", type: "text" },
                          { label: "Supervisor", name: "supervisor", type: "text" },
                          { label: "Área", name: "area", type: "text" }
                        ].map(field => (
                          <div className="formInput" key={field.name}>
                            <label>{field.label}</label>
                            <input
                              type={field.type}
                              name={field.name}
                              value={form[field.name]}
                              onChange={handleChange}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="formSection">
                    <div className="sectionHeader" onClick={() => toggleSection('otros')}>
                      <h3>Otros Datos</h3>
                      <ExpandMore className={`expandIcon ${expandedSections.otros ? 'expanded' : ''}`} />
                    </div>
                    {expandedSections.otros && (
                      <div className="sectionContent">
                        {[
                          { label: "LinkedIn", name: "linkedIn", type: "text" },
                          { label: "Idioma", name: "language", type: "text" }
                        ].map(field => (
                          <div className="formInput" key={field.name}>
                            <label>{field.label}</label>
                            <input
                              type={field.type}
                              name={field.name}
                              value={form[field.name]}
                              onChange={handleChange}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Campo de contraseña */}
                  <div className="formSection">
                    <div className="sectionHeader" onClick={() => toggleSection('seguridad')}>
                      <h3>Seguridad</h3>
                      <ExpandMore className={`expandIcon ${expandedSections.seguridad ? 'expanded' : ''}`} />
                    </div>
                    {expandedSections.seguridad && (
                      <div className="sectionContent">
                        <div className="formInput">
                          <label>Nueva contraseña</label>
                          <input
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleChange}
                            placeholder="Dejar vacío si no deseas cambiarla"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <button 
                    type="submit" 
                    className="updateButton"
                    disabled={!hasChanges}
                  >
                    Guardar cambios
                  </button>
                  {hasChanges && (
                    <div className="changesNotification">
                      Tienes cambios pendientes. Presiona guardar para aplicarlos.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {showWarningModal && (
        <div className="modalOverlay">
          <div className="warningModal">
            <h2>Cambios sin guardar</h2>
            <p>Tienes cambios pendientes en tu perfil. ¿Qué deseas hacer?</p>
            <div className="modalActions">
              <button 
                className="btnCancel"
                onClick={() => setShowWarningModal(false)}
              >
                Seguir editando
              </button>
              <button 
                className="btnAbandon"
                onClick={handleConfirmAbandon}
              >
                Descartar cambios
              </button>
              <button 
                className="btnSave"
                onClick={handleSaveAndNavigate}
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modalOverlay successOverlay">
          <div className="successModal">
            <div className="successIcon">✓</div>
            <h2>¡Guardado Exitoso!</h2>
            <p>{successMessage}</p>
          </div>
        </div>
      )}

      {showErrorModal && (
        <div className="modalOverlay errorOverlay">
          <div className="errorModal">
            <div className="errorIcon">✕</div>
            <h2>Error al guardar</h2>
            <p>{errorMessage}</p>
            <button 
              onClick={() => setShowErrorModal(false)}
              className="errorCloseBtn"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
